// discount-keeper-final.js
// Place at bottom of theme.liquid (or include as module) — runs once and exposes API.
//
// NO `import` IN THIS FILE, for the same reason as assets/apply-discount-cards.js: bare specifiers
// need the import map (Safari 16.4+), and one unresolved import would stop the code reaching the
// cart at all. The `cart:update` it dispatches is therefore a plain CustomEvent shaped like
// CartUpdateEvent (assets/events.js).
(function () {
    // ---------- Helpers ----------
    const isSameOriginHref = (href) => {
        try {
            const url = new URL(href, location.href);
            return url.origin === location.origin;
        } catch {
            return false;
        }
    };

    const isSafeToModify = (href) => {
        if (!href) return false;
        // skip mailto: tel: javascript: fragment-only anchors
        if (/^(mailto:|tel:|javascript:|#)/i.test(href)) return false;
        // skip cart/checkout explicit paths
        if (/^\/cart|\/checkout/i.test(href)) return false;
        // only same-origin links
        return isSameOriginHref(href);
    };

    const getDiscountFromThemeOrUrl = () => {
        const param = new URL(location.href).searchParams.get('discount');
        return param ? param.toUpperCase() : null;
    };

    // ---------- Cart ----------
    // The code is checked against the CART, not against the `discount_code` cookie. The two drift
    // apart: a new cart (after an order, or an expired one) starts with no code while the cookie is
    // still set, so this script skipped it, the drawer priced everything at list and only the
    // checkout applied the code from the cookie. Measured live 2026-10-15: AirMini $689.25 on the
    // card, $919.00 in the drawer, $689.25 at checkout.

    /** @returns {Promise<any>} the cart, or null when it could not be read */
    const readCart = async () => {
        try {
            // `no-store`: a page reached with Back/Forward can otherwise read the cart of the earlier visit.
            const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
            return response.ok ? await response.json() : null;
        } catch {
            return null;
        }
    };

    const cartHasCode = (cart, code) =>
        Array.isArray(cart?.discount_codes) &&
        cart.discount_codes.some((entry) => String(entry.code).toUpperCase() === code);

    /**
     * Puts a code on the cart and returns the cart that results.
     *
     * `/discount/CODE` stores the code on the cart AND in the `discount_code` cookie, which is what a
     * checkout that never sees the cart reads (Buy it now, Shop Pay). Its default redirect is the
     * home page, which a plain fetch downloaded in full in the background (1.36 MB, 2.6 s measured
     * live); `?redirect=/cart.js` lands on the cart instead, so one request applies the code and
     * returns the result.
     * @returns {Promise<any>} the cart, or null when the request failed
     */
    const applyCodeToCart = async (code) => {
        try {
            const response = await fetch(
                `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`,
                { credentials: 'same-origin', cache: 'no-store' }
            );
            return response.ok ? await response.json() : null;
        } catch {
            return null;
        }
    };

    // sessionStorage can throw (private mode, blocked storage); every use is optional.
    const storage = {
        get: (key) => { try { return sessionStorage.getItem(key); } catch { return null; } },
        set: (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* optional */ } },
    };

    /**
     * Tells the drawer and the cart icon that the cart changed under them. Same shape as
     * CartUpdateEvent; `background` keeps the drawer closed (assets/cart-drawer.js → #handleCartAdd),
     * and `component-cart-items.js` re-renders its section because there is no `sections` payload.
     */
    const announceCart = (cart) => {
        document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
                resource: cart,
                sourceId: 'discount-keeper',
                data: { source: 'discount-keeper', itemCount: cart.item_count, background: true },
            },
        }));
    };

    /** Attempts per code per cart. Bounds a code Shopify keeps refusing, and a shopper who removed it. */
    const MAX_ATTEMPTS = 3;

    /**
     * How long after an attempt the cart is checked again. Other scripts write the cart right after
     * load — Gorgias (XHR) and Elevar post `/cart/update.js` — and a write that STARTED before ours
     * lands after it with the cart as it was, taking the code back off
     * (memory/cart-ajax-writes-do-not-serialize.md). Measured on the preview 2026-10-15: BEDTIME
     * applied 3.7→4.7 s, Gorgias' update 4.4→5.4 s, and the code was gone even though a read at ~4.8 s
     * had confirmed it. A throttled request (429) fails outright and needs the same second look.
     */
    const VERIFY_DELAY = 2500;

    /**
     * Puts one code on the cart unless it is there already or has used up its attempts on this cart.
     * Whether it stayed is decided by the next pass (VERIFY_DELAY), not by the response: a failed
     * request or a clobbered write leaves an attempt for later instead of giving up for the session.
     * @returns {Promise<{ cart: any, attempted: boolean }>}
     */
    const ensureCode = async (cart, code) => {
        if (cartHasCode(cart, code)) return { cart, attempted: false };

        const attemptsKey = `sl-discount-attempts:${cart.token}:${code}`;
        const attempts = Number(storage.get(attemptsKey)) || 0;
        if (attempts >= MAX_ATTEMPTS) return { cart, attempted: false };
        storage.set(attemptsKey, String(attempts + 1));

        return { cart: (await applyCodeToCart(code)) || cart, attempted: true };
    };

    /**
     * Makes sure each code is on the cart, then re-renders the drawer if prices moved.
     *
     * The drawer is rendered with the page; a code that reaches the cart afterwards changed the
     * prices under it and nothing redrew it (measured live 2026-10-15: drawer subtotal $1,004.00,
     * Navidium's button in the same drawer "$711.75", the cart $699).
     *
     * Several codes on one cart are safe: when they do not combine, Shopify applies the one that is
     * best for the shopper whatever the order (measured live 2026-10-15: MASK30 and BEDTIME on the
     * same mask gave MASK30's $128.80 in both orders, and re-applying BEDTIME did not displace it).
     * Writes are sequential, because cart writes on this store clobber each other when they overlap
     * (memory/cart-ajax-writes-do-not-serialize.md). The LAST code applied is the one left in the
     * `discount_code` cookie, which an express checkout (Buy it now, Shop Pay) reads.
     *
     * After any attempt the cart is looked at once more, VERIFY_DELAY later, and the pass repeats if
     * a code is missing; MAX_ATTEMPTS per code per cart ends it.
     *
     * @param {string[]} codes
     * @param {{ cart?: any }} [options] - `cart`: one already read, to skip the read
     */
    const ensureCodesInCart = async (codes, { cart: known = null } = {}) => {
        const before = known || (await readCart());
        if (!before) return;

        let cart = before;
        let attempted = false;
        for (const code of codes) {
            const result = await ensureCode(cart, code);
            cart = result.cart;
            attempted = attempted || result.attempted;
        }

        // The cart the redirect lands on can come back without the code: measured on the preview
        // 2026-10-15, another app's `/cart/update.js` overlapped the apply and the code was gone.
        // Confirm with a fresh read before deciding what to redraw.
        if (attempted && codes.some((code) => !cartHasCode(cart, code))) cart = (await readCart()) || cart;

        if (cart !== before && cart.item_count > 0 && cart.total_price !== before.total_price) {
            announceCart(cart);
        }

        if (attempted) setTimeout(() => ensureCodesInCart(codes), VERIFY_DELAY);
    };

    // ---------- Per-link / Container update ----------
    const applyDiscountToLink = (linkEl, code) => {
        if (!(linkEl instanceof HTMLAnchorElement) || !code) return false;

        const href = linkEl.getAttribute('href');
        if (!isSafeToModify(href)) return false;
        // Avoid reapplying the same code
        if (linkEl.dataset.discountApplied === code) return false;
        
        try {
            const url = new URL(href, location.href);
            url.searchParams.set('discount', code);
            // Keep same-origin links relative to avoid absolute churn
            const relativeHref = url.origin === location.origin
                ? (url.pathname + url.search + url.hash)
                : url.href;

            linkEl.setAttribute('href', relativeHref);

            // mark as processed
            linkEl.dataset.discountApplied = code;
            return true;
        } catch {
            return false;
        }
    };

    /**
     * Update only anchors within `root` (Element or Document). Returns number updated.
     * If root omitted, updates entire document.
     */
    const applyDiscountToContainer = (code, root = document) => {
        if (!code) return 0;

        const container = (root instanceof Element || root instanceof Document) ? root : document;
        const anchors = container.querySelectorAll('a[href]');
        let updated = 0;

        for (const a of anchors) {
            if (applyDiscountToLink(a, code)) updated++;
        }
        return updated;
    };

    // ---------- Core: schedule ----------
    const scheduleIdle = (fn) => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(fn, { timeout: 1500 });
        } else {
            setTimeout(fn, 30);
        }
    };

    // ---------- Public API ----------
    /**
     * Run update for the whole page (non-blocking).
     * Safe to call multiple times — per-link guard prevents rework.
     */
    const keepDiscountCode = () => {
        const code = getDiscountFromThemeOrUrl();
        if (!code) return 0;

        scheduleIdle(() => {
            ensureCodesInCart([code]);
            return applyDiscountToContainer(code, document);
        });
        return 0;
    };

    /**
     * Update only a subtree (Element) or whole document when no arg passed.
     * Useful to call right after you insert new HTML.
     * Returns number of links updated synchronously.
     */
    const keepDiscountCodeOn = (root = document) => {
        const code = getDiscountFromThemeOrUrl();
        if (!code) return 0;
        return applyDiscountToContainer(code, root);
    };

    // Debounced wrapper for repeated calls targeted at same root.
    const _debounceMap = new WeakMap();
    const keepDiscountCodeDebounced = (root = document, wait = 150) => {
        // clear prior timer for same root
        const prev = _debounceMap.get(root);
        if (prev) clearTimeout(prev);

        const t = setTimeout(() => {
            keepDiscountCodeOn(root);
            _debounceMap.delete(root);
        }, wait);

        _debounceMap.set(root, t);
    };

    // ---------- Auto-run on initial load ----------
    // Script expected at bottom of body; still schedule on idle for safety
    (function autoRun() {
        const { storeDiscountCode, windowUrlDiscountCode, is_customer } = Theme?.discount || {};
        let code = windowUrlDiscountCode;
        // If user is logged in and no code from url, use code from theme if available
        if (is_customer && !code && storeDiscountCode) {
            code = storeDiscountCode;
        }
        if (!code) return; // nothing to do

        // A logged-in shopper is entitled to the store code, and their cards are priced with it
        // (assets/apply-discount-cards.js gives every customer card the store percentage). A code
        // from the URL used to replace it outright, so an expired link left the cart with no valid
        // code at all: `?discount=LABOR` measured live 2026-10-15 showed the AirSense 11 at $753 on
        // the card and charged $1,004 in the cart and at checkout. The store code goes on as well;
        // Shopify keeps whichever of the two is better for the shopper. It goes on FIRST, so the
        // URL's code is the one left in the cookie an express checkout reads, as before.
        const codes = is_customer && storeDiscountCode && storeDiscountCode !== code ? [storeDiscountCode, code] : [code];

        scheduleIdle(async () => {
            applyDiscountToContainer(code, document);
            // assets/custom-cart-icon.js already read the cart when the header connected; reuse it.
            const known = await (window.Theme?.cartOnLoad ?? null);
            ensureCodesInCart(codes, { cart: known });
        });
    })();

    // re-run on browser back/forward
    window.addEventListener('popstate', () => {
        setTimeout(() => keepDiscountCode(), 50);
    });

    // ---------- expose API ----------
    window.Theme = window.Theme || {};
    // whole-page run (non-blocking)
    window.Theme._keepDiscountCode = keepDiscountCode;
    // immediate subtree update (sync)
    window.Theme._keepDiscountCodeOn = keepDiscountCodeOn;
    // debounced subtree update
    window.Theme._keepDiscountCodeDebounced = keepDiscountCodeDebounced;
    // done
})();
